import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {CameraIcon, CheckCircleIcon, XCircleIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {DownloadIcon, EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import Dropdown from "components/Dropdown";
import {Frame} from "types/Frame";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import {useOperator} from "context/OperatorContext";
import DeleteFrame from '../Delete';

function FrameList() {
    const [page, setPage] = useState<number>(1);
    const result = useQuery(['frames.all', {page}], () => ngage.frames.all(page), {keepPreviousData: true})

    const [deletingFrame, setDeletingFrame] = useState<Frame | null>(null);

    const {subdomain} = useOperator()
    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Prefetch next and previous pages
    prefetchNearbyPages('frames.all', page, ngage.frames.all)

    // Prepare page actions
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/frames/new')}>New</Button>

    // Prepare breadcrumbs
    const breadcrumbs = {
        'App Content': '/',
        'Interactive': '/',
        'Photo Frames': '/frames'
    };

    return (
        <Page
          title="Photo Frames"
          subTitle='Customisable photo frames to help capture the day'
          actions={actions}
          breadcrumbs={breadcrumbs}
        >
            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.CENTER}>Published</TableHeader>
                        <TableHeader>Added</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>

                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={4}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <CameraIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No frames</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : result?.data?.data.map((frame, index) => (
                        <TableRow key={frame.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        {/* TODO: Convert to component */}
                                        <Dropdown position="left">
                                            {[
                                                {name: 'View', icon: EyeIcon, onClick: () => navigate(`/frames/${frame.id}`)},
                                                {name: 'Download', icon: DownloadIcon, url: ngage.frames.downloadUrl(frame.id, subdomain), download: true},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/frames/${frame.id}/edit`)},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingFrame(frame)}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <CameraIcon className="h-5 w-5 mr-2 text-gray-500" /> {frame.name}
                                    </div>
                                </div>
                            </TableData>
                            <TableData alignment={TableData.alignments.CENTER}>
                                {frame.is_published? <CheckCircleIcon className="h-5 w-5 text-green-600 inline"/> : <XCircleIcon className="h-5 w-5 text-red-600 inline"/>}
                            </TableData>
                            <TableData>{formattedDateTime(frame.created_at)}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {/* TODO: Convert to component */}
                                <Dropdown>
                                    {[
                                        {name: 'View', icon: EyeIcon, onClick: () => navigate(`/frames/${frame.id}`)},
                                        {name: 'Download', icon: DownloadIcon, url: ngage.frames.downloadUrl(frame.id, subdomain), download: true},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/frames/${frame.id}/edit`)},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingFrame(frame)}
                                    ]}
                                </Dropdown>
                            </TableData>

                            {/* Delete confirmation modal */}
                            <DeleteFrame
                                frame={deletingFrame}
                                show={frame === deletingFrame}
                                onCancel={() => setDeletingFrame(null)}
                                onDelete={frame => {
                                    navigate('/frames')
                                    notify("success", "Frame Deleted", `${frame.name} has been permanently removed`)
                                }}
                                onDeleteFail={frame => {
                                    setDeletingFrame(null)
                                    notify("error", "Frame Not Deleted", `${frame.name} could not be removed`)
                                }}
                            />
                        </TableRow>
                    ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default FrameList;